import { useContentElementProgress } from "@/hooks/useContentElementProgress";
import { useInteractiveElementState } from "@/hooks/useInteractiveElementState";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import { StatusIcon } from "@/components/ui/status-icon";
import { LessonStatus } from "@/components/course/tree/types";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { StoryboardRenderer } from "@/components/storyboard/StoryboardRenderer";
import { cn } from "@/lib/utils";

const INTERACTIVE_BADGE_STATUS: Record<
  InteractiveElementStatusKind,
  LessonStatus
> = {
  [InteractiveElementStatusKind.Completed]: LessonStatus.Completed,
  [InteractiveElementStatusKind.InProgress]: LessonStatus.InProgress,
  [InteractiveElementStatusKind.NotStarted]: LessonStatus.NotStarted,
};

const INTERACTIVE_TEXT_SUBTYPES = new Set([
  "WORKSHEET",
  "SELF_REFLECTION_QUESTIONS",
  "EXPERIENCE_REFLECTION",
]);

const usesInteractiveProgress = (storyboard: Storyboard): boolean =>
  storyboard.contentType === "QUIZ" ||
  storyboard.contentType === "WORKSHEET" ||
  (storyboard.contentType === "TEXT" &&
    INTERACTIVE_TEXT_SUBTYPES.has(storyboard.subType));

const isFlashcardsStoryboard = (storyboard: Storyboard): boolean =>
  storyboard.contentType === "TEXT" && storyboard.subType === "FLASHCARDS";

/**
 * Corner badge for quizzes, worksheets, and reflections — server completed
 * wins (e.g. a reflection finished by scroll before that path was removed),
 * otherwise interactive status.
 */
const interactiveBadgeStatus = (
  interactiveKind: InteractiveElementStatusKind,
  serverCompleted: boolean
): LessonStatus => {
  if (serverCompleted) return LessonStatus.Completed;
  return INTERACTIVE_BADGE_STATUS[interactiveKind];
};

const TrackedContentElement = ({
  el,
  storyboard,
}: {
  el: CourseTreeNode;
  storyboard: Storyboard;
}) => {
  // Quizzes, worksheets, and reflections report progress when answered;
  // Lernkarten complete after every card is flipped. None of those should
  // auto-complete by merely scrolling into view.
  const isInteractiveProgress = usesInteractiveProgress(storyboard);
  const isFlashcards = isFlashcardsStoryboard(storyboard);
  const { sentinelRef, isCompleted } = useContentElementProgress(el.id, {
    autoComplete: !isInteractiveProgress && !isFlashcards,
  });
  const { enrollmentId, trainerMode } = useCourseDetailStore();
  const { status: interactiveStatus } = useInteractiveElementState(
    el.id,
    enrollmentId
  );

  // Interactive types use local task status (plus server completed). Lernkarten
  // and other content follow LearningProgress / completedContentElementIds.
  let badgeStatus: LessonStatus;
  if (isInteractiveProgress) {
    badgeStatus = interactiveBadgeStatus(interactiveStatus.kind, isCompleted);
  } else if (isCompleted) {
    badgeStatus = LessonStatus.Completed;
  } else {
    badgeStatus = LessonStatus.InProgress;
  }

  return (
    <div id={`element-${el.id}`} className="relative scroll-mt-96">
      {enrollmentId && !trainerMode && (
        <div
          key={badgeStatus}
          className={cn(
            "absolute top-0 right-0 z-10 flex size-6 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-sm [animation:icon-pop_0.35s_ease-out]",
            badgeStatus === LessonStatus.Completed && "border-2 border-success"
          )}
        >
          <StatusIcon status={badgeStatus} />
        </div>
      )}
      <StoryboardRenderer contentElement={el} storyboard={storyboard} />
      {/* Viewed-detection marker: the element counts as seen once its end
          has been on screen (see useContentElementProgress) */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
    </div>
  );
};

export default TrackedContentElement;
